import { useState } from "react";
import { Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { api, CONTACT_EMAIL, errorMessage, WHATSAPP_DISPLAY, WHATSAPP_LINK } from "@/lib/api";
import { Field, Input, Textarea, useForm } from "@/components/site/Fields";
import { Reveal } from "@/components/site/Motion";

export const ContactForm = ({ type = "contact", vehicle, vehicleId, light, compact }) => {
  const { t, lang } = useLang();
  const [v, setV, bind] = useForm({ name: "", email: "", phone: "", whatsapp: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/leads/${type}`, { ...v, vehicle: vehicle || "", vehicle_id: vehicleId || "", lang });
      toast.success(t("f_sent"));
      setV({ name: "", email: "", phone: "", whatsapp: "", message: "" });
    } catch (err) {
      toast.error(errorMessage(err) || t("f_error"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid={`${type}-form`} className={light ? "text-[#0e1013]" : ""}>
      <div className="grid gap-6 sm:grid-cols-2">
        <Field label={t("f_name")} required><Input data-testid={`${type}-name`} required minLength={2} {...bind("name")} /></Field>
        <Field label={t("f_email")} required><Input data-testid={`${type}-email`} type="email" required {...bind("email")} /></Field>
        <Field label={t("f_phone")}><Input data-testid={`${type}-phone`} type="tel" {...bind("phone")} /></Field>
        <Field label={t("f_whatsapp")}><Input data-testid={`${type}-whatsapp`} type="tel" {...bind("whatsapp")} /></Field>
        <Field label={t("f_message")} className="sm:col-span-2"><Textarea data-testid={`${type}-message`} rows={compact ? 3 : 4} {...bind("message")} /></Field>
      </div>
      <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
        <span className="mono text-[0.58rem] text-[#8b95a5]">* {t("f_required")}</span>
        <button type="submit" disabled={busy} data-testid={`${type}-submit`} className={`btn ${light ? "btn-dark" : "btn-primary"} disabled:opacity-60`}>
          {busy ? "…" : type === "inquiry" ? t("d_request") : t("f_send")}
        </button>
      </div>
    </form>
  );
};

export const ContactBlock = ({ s }) => {
  const { t, bi } = useLang();
  return (
    <section className="section relative bg-[#070809]" data-testid="contact-section" id="contact">
      <div className="container-x grid gap-14 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <h2 className="display text-3xl sm:text-4xl lg:text-5xl">{t("contact_title")}</h2>
          <p className="mt-6 max-w-md text-base leading-relaxed text-[#c7ccd4]">{t("co_copy")}</p>
          <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-cta" className="btn btn-whatsapp"><MessageCircle size={15} /> {s ? bi(s.contact_cta) : t("contact_cta_wa")}</a>
            <a href={`mailto:${CONTACT_EMAIL}`} data-testid="contact-email-cta" className="btn btn-ghost"><Mail size={15} /> {t("contact_cta_email")}</a>
          </div>
          <div className="mt-12 grid gap-6 border-t border-white/10 pt-8 sm:grid-cols-2">
            <div><p className="eyebrow mb-3">WhatsApp</p><a href={WHATSAPP_LINK} className="link-underline text-sm">{WHATSAPP_DISPLAY}</a></div>
            <div><p className="eyebrow mb-3">Email</p><a href={`mailto:${CONTACT_EMAIL}`} className="link-underline break-all text-sm">{CONTACT_EMAIL}</a></div>
          </div>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-6 lg:col-start-7"><ContactForm /></Reveal>
      </div>
    </section>
  );
};
